import React from 'react';
import type { ComponentConfig } from '@puckeditor/core';
import styled from 'styled-components';

import { mediaQueries } from 'src/theme/constants/media';

import { createSpacingFields } from '../fields/spacing-fields';
import {
  CONTAINER_TEXT_COLOR_AI_SCHEMA,
  CONTAINER_TEXT_COLOR_OPTIONS,
  containerColorStyle,
  INHERIT_COLOR,
} from '../fields/container-colors';
import type { ContainerTextColor } from '../fields/container-colors';
import { BLOCK_SPACING, BLOCK_WIDTHS, CONTAINER_MIN_HEIGHTS, lookup, spacingToCss } from '../fields/tokens';
import type { BlockSpacing, BoxSpacing } from '../fields/tokens';
import { selectField } from '../fields/widgets/select-field';
import { BackgroundField, backgroundStyle, BACKGROUND_AI_SCHEMA, DEFAULT_BACKGROUND } from '../fields';
import type { BackgroundValue } from '../fields';

import type { SlotProps } from './slot';

// Intent-based direction options. Dropped values (row-reverse, column-reverse) are no longer
// offered but still render verbatim when they come from saved data — see render passthrough.
const DIRECTION_OPTIONS = [
  { label: 'Horizontal', value: 'row' },
  { label: 'Vertical', value: 'column' },
] as const;

const WRAP_OPTIONS = [
  { label: 'No wrap', value: 'nowrap' },
  { label: 'Wrap', value: 'wrap' },
] as const;

// Intent-based align options. Dropped values (baseline) still render verbatim from saved data.
const ALIGN_ITEMS_OPTIONS = [
  { label: 'Fill', value: 'stretch' },
  { label: 'Start', value: 'flex-start' },
  { label: 'Center', value: 'center' },
  { label: 'End', value: 'flex-end' },
] as const;

// Intent-based distribution options. Dropped values (space-around, var(--justify-content))
// still render verbatim from saved data.
const JUSTIFY_CONTENT_OPTIONS = [
  { label: 'Start', value: 'flex-start' },
  { label: 'Center', value: 'center' },
  { label: 'End', value: 'flex-end' },
  { label: 'Space between', value: 'space-between' },
  { label: 'Even spacing', value: 'space-evenly' },
] as const;

const GAP_OPTIONS: { label: string; value: BlockSpacing }[] = [
  { label: 'Extra small (8px)', value: 'xs' },
  { label: 'Small (16px)', value: 'sm' },
  { label: 'Medium (24px)', value: 'md' },
  { label: 'Large (40px)', value: 'lg' },
  { label: 'Extra large (64px)', value: 'xl' },
  { label: '2X large (96px)', value: 'xxl' },
];

const WIDTH_OPTIONS = [
  { label: 'Fit content', value: 'fit' },
  { label: 'Fill available', value: 'fill' },
] as const;

const MOBILE_STACK_OPTIONS = [
  { label: 'Stack vertically', value: 'stack' },
  { label: 'Keep side by side', value: 'keep' },
] as const;

// How the container sizes its direct children. 'content' keeps each child at its natural width;
// 'equal' gives every child an identical share of the container, ignoring content width.
const ITEM_SIZING_OPTIONS = [
  { label: 'Fit content', value: 'content' },
  { label: 'Equal width', value: 'equal' },
] as const;

const ITEM_SIZING_FLEX: Record<string, string> = {
  content: '0 1 auto',
  equal: '1 1 0',
};

const MIN_HEIGHT_OPTIONS = [
  { label: 'Auto', value: 'auto' },
  { label: 'Medium', value: 'medium' },
  { label: 'Tall', value: 'tall' },
  { label: 'Full screen', value: 'full' },
] as const;

type MobileStack = typeof MOBILE_STACK_OPTIONS[number]['value'];

export type FlexLayoutProps = {
  /** Puck injects this slot component at render time; omitted from defaultProps. */
  content?: React.FC<SlotProps>;
  /**
   * Optional because a page saved before this control existed has no `background` key, and Puck
   * does not inject `defaultProps` in the published Render path — so `undefined` genuinely reaches
   * render there. The type says so rather than the render guard being lint-suppressed.
   */
  background?: BackgroundValue;
  /** Pushes a text colour onto every descendant via a scoped custom property. */
  textColor: ContainerTextColor;
  /**
   * CSS flex-direction value. Offered options are 'row'/'column'; dropped options
   * ('row-reverse', 'column-reverse') still render verbatim when they come from saved data.
   */
  direction: string;
  /**
   * CSS flex-wrap value. Offered options are 'nowrap'/'wrap'; dropped options
   * ('wrap-reverse') still render verbatim when they come from saved data.
   */
  wrap: string;
  /**
   * A BLOCK_SPACING key (xs/sm/md/lg/xl/xxl), or a raw CSS length carried over from before this was
   * a token (kept-key collision: `gap` existed pre-refactor as a raw CSS string).
   */
  gap: BlockSpacing | string;
  /**
   * CSS align-items value. Offered options are 'stretch'/'flex-start'/'center'/'flex-end';
   * dropped option ('baseline') still renders verbatim when it comes from saved data.
   */
  alignItems: string;
  /**
   * CSS justify-content value. Offered options omit 'space-around' and the old
   * 'var(--justify-content)' entry; both still render verbatim from saved data.
   */
  justifyContent: string;
  /**
   * On-mobile layout behaviour: 'stack' collapses to a vertical column below the
   * largePhone breakpoint; 'keep' preserves the chosen desktop direction.
   * Default 'stack' because most horizontal rows need to become vertical on phones.
   */
  mobileStack: MobileStack;
  /**
   * How direct children are sized. 'content' leaves each at its natural width; 'equal' gives every
   * child an identical share of the container. Set on the container because Puck wraps each child
   * in its own div, so a child cannot claim the container's spare space by itself.
   */
  itemSizing: string;
  /**
   * Grouped four-sided padding. Optional because pages saved before the grouped field carry a raw CSS
   * string under this same key (kept-key collision: `padding` kept its key but changed from a raw CSS
   * string to a BoxSpacing object).
   */
  padding?: BoxSpacing | string;
  /** Grouped four-sided margin. */
  margin?: BoxSpacing;
  /**
   * A BLOCK_WIDTHS key ('fit'/'fill'), or a raw CSS length carried over from before this was a token
   * (kept-key collision: `width` kept its key but changed meaning).
   */
  width: string;
  /**
   * A CONTAINER_MIN_HEIGHTS key, or a raw CSS length carried over from before this was a token
   * (kept-key collision: `minHeight` kept its key but is now a preset rather than a raw string).
   * Resolves via literal passthrough so saved '400px' or the old '0' sentinel still render as authored.
   */
  minHeight: string;
};

const DEFAULT_PADDING: BoxSpacing = { top: '0px', right: '0px', bottom: '0px', left: '0px' };
const DEFAULT_MARGIN: BoxSpacing = { top: '0px', right: '0px', bottom: '0px', left: '0px' };

/**
 * Styled slot element for FlexLayout. Owns `flex-direction` via CSS custom properties so a
 * media-query rule can override it — an inline style cannot be overridden by a media query
 * without `!important`. All other flex properties are still set inline; only `flex-direction`
 * must live in the stylesheet so the mobile breakpoint can win.
 *
 * This component is passed as `as={StyledFlexSlot}` to the Puck slot, which passes its own
 * `className` through so styled-components' generated class lands on the same element.
 *
 * Pattern adapted from `spacer.tsx`, which uses the same custom-property + media-query pattern
 * for `--cms-spacer-height` / `--cms-spacer-mobile-height`.
 *
 * Exported for testing: `toHaveStyleRule` from jest-styled-components inspects the generated
 * stylesheet and requires a rendered styled-component instance.
 */
export const StyledFlexSlot = styled.div`
  flex-direction: var(--cms-flex-direction, row);

  /*
   * Child sizing has to be a rule on the container, not a control on the child. Puck renders a
   * wrapper div around every component in a slot (see cms/AGENTS.md gotcha #2 - an inline:true
   * component would replace it, and no CMS component sets that), so the flex items are Puck's
   * wrappers, not the authored Button or Heading. A child's own "Width: fill" therefore fills a
   * wrapper that is itself sized to its content, and the child cannot claim the container's spare
   * space. Only the container can grant it, by setting flex on its direct children.
   *
   * This is also why the removed flexGrow prop could never have solved it: it set flex-grow on the
   * container itself, which does nothing unless the container is a flex item of something else.
   *
   * NOTE: no backticks in this comment - it lives inside a tagged template literal, and a stray
   * backtick terminates the CSS and breaks the module.
   */
  & > * {
    flex: var(--cms-flex-item-flex, 0 1 auto);
  }

  @media ${mediaQueries.largePhone} {
    flex-direction: var(--cms-flex-mobile-direction, column);

    /*
     * The item flex has to be swapped at the breakpoint alongside the direction. flex-basis applies
     * to the MAIN axis, so "1 1 0" equalises width in a row but HEIGHT in a column - a stacked
     * container would stretch its children into tall blocks rather than leaving them full width.
     * The mobile value is therefore computed against the mobile direction, not the desktop one.
     */
    & > * {
      flex: var(--cms-flex-item-mobile-flex, 0 1 auto);
    }
  }
`;

/**
 * Generic flex layout component for Puck editors.
 * Provides a configurable flexbox container with a content slot
 * that child components can be dropped into.
 *
 * Usage in any editor config:
 *   import { flexLayoutConfig } from 'src/cms/components/flex-layout';
 *   components: { FlexLayout: flexLayoutConfig, ... }
 */
export const flexLayoutConfig: ComponentConfig<FlexLayoutProps> = {
  label: 'Flex Layout',
  ai: {
    instructions:
      "A flexbox container for arranging child components in rows or columns. IMPORTANT: Always add child components INTO this component's content slot — never leave it empty. Use inside a Section when you need multi-column layouts or horizontal arrangements.",
  },
  fields: {
    content: { type: 'slot' },
    background: {
      type: 'custom',
      label: 'Background',
      render: BackgroundField,
      ai: {
        schema: BACKGROUND_AI_SCHEMA as Record<string, unknown>,
        instructions:
          "Background styling. Set color to a surface role ('none', 'surface', 'muted') so it follows merchant branding; use a hex value only when a specific brand colour is required. Optionally set image URL with size/position/repeat.",
      },
    },
    textColor: {
      type: 'select',
      label: 'Text color',
      options: [...CONTAINER_TEXT_COLOR_OPTIONS],
      ai: {
        schema: (CONTAINER_TEXT_COLOR_AI_SCHEMA as unknown) as Record<string, unknown>,
        instructions:
          "Text colour applied to everything inside this flex container. Leave as 'inherit' unless the container has a dark or coloured background.",
      },
    },
    direction: selectField(
      'Direction',
      DIRECTION_OPTIONS,
      "Layout direction. 'Horizontal' arranges children side by side; 'Vertical' stacks them top to bottom."
    ),
    wrap: selectField(
      'Wrap',
      WRAP_OPTIONS,
      "'Wrap' lets children flow onto the next line when they run out of space — the safe default for responsive layouts."
    ),
    mobileStack: selectField(
      'On mobile',
      MOBILE_STACK_OPTIONS,
      "'Stack vertically' collapses a horizontal row to a single column on phones — almost always correct for navigation rows and card grids. 'Keep side by side' preserves the desktop direction on all screen sizes."
    ),
    itemSizing: selectField(
      'Item sizing',
      ITEM_SIZING_OPTIONS,
      "'Fit content' keeps each child at its natural width. 'Equal width' makes every child share the container equally, which is how you get buttons or cards to fill the row instead of hugging their text."
    ),
    gap: {
      type: 'select',
      label: 'Gap',
      options: GAP_OPTIONS,
      ai: {
        schema: { type: 'string', enum: GAP_OPTIONS.map(({ value }) => value) },
        instructions:
          "Space between flex children from the shared rhythm scale. 'sm' (16px) suits tight grids; 'md' (24px) suits section-level layouts.",
      },
    },
    alignItems: selectField(
      'Align',
      ALIGN_ITEMS_OPTIONS,
      "Cross-axis alignment of flex children. 'Fill' stretches every child to the container height; 'Start'/'End' pin items to the near or far edge; 'Center' balances them in the middle."
    ),
    justifyContent: selectField(
      'Distribute',
      JUSTIFY_CONTENT_OPTIONS,
      "Main-axis distribution of flex children. 'Start' packs them at the beginning; 'Space between' spreads them to both edges; 'Even spacing' puts equal gaps around every child."
    ),
    minHeight: selectField(
      'Min height',
      MIN_HEIGHT_OPTIONS,
      "'Auto' lets the container collapse to its content. 'Medium' (~320px) suits a content band; 'Tall' (~480px) suits a hero or feature section; 'Full screen' fills the viewport height."
    ),
    padding: {
      type: 'object',
      label: 'Padding',
      objectFields: createSpacingFields(),
    },
    margin: {
      type: 'object',
      label: 'Margin',
      objectFields: createSpacingFields(),
    },
    width: {
      type: 'select',
      label: 'Width',
      options: [...WIDTH_OPTIONS],
      ai: {
        schema: { type: 'string', enum: Object.keys(BLOCK_WIDTHS) },
        instructions: "'fill' makes the container take all available width; 'fit' shrinks to its content.",
      },
    },
  },
  defaultProps: {
    // FlexLayout is an inner layout container, not a page band, so it must default to transparent.
    // Puck injects defaultProps in the editor but NOT in the published Render path, so a legacy saved
    // page with no `background` key reaches render with `background === undefined`. The conditional
    // spread below guards against that; this default ensures a newly-authored FlexLayout also starts
    // transparent rather than painting a white fill over whatever sits behind it.
    background: { ...DEFAULT_BACKGROUND, color: 'none' },
    textColor: INHERIT_COLOR,
    direction: 'row',
    wrap: 'wrap',
    // `mobileStack` is a NEW key, so a legacy saved page has no value for it and (because Puck does
    // not inject defaultProps in the published Render path) reaches render as undefined, taking the
    // destructure default below. 'stack' therefore DOES restyle already-published pages: a
    // deliberately side-by-side row now collapses to a column at <=769px without the author
    // revisiting it. That is intended. An unstacked row is the single most common way a page breaks
    // on a phone, so stacking is the correct default even though it changes existing pages; the
    // alternative ('keep') would preserve them exactly at the cost of making the feature opt-in and
    // leaving every existing page broken on mobile. The render default below must stay identical to
    // this value, or the editor and the live page disagree.
    mobileStack: 'stack',
    // Contrast with `itemSizing`, also a new key: 'content' IS the pre-existing behaviour, so
    // legacy pages are untouched by it.
    itemSizing: 'content',
    gap: 'xs',
    alignItems: 'stretch',
    justifyContent: 'flex-start',
    minHeight: 'auto',
    padding: DEFAULT_PADDING,
    margin: DEFAULT_MARGIN,
    width: 'fill',
  },
  render: ({
    content: slotContent,
    background,
    textColor,
    direction = 'row',
    wrap,
    mobileStack = 'stack',
    itemSizing = 'content',
    gap,
    alignItems,
    justifyContent,
    minHeight = 'auto',
    padding,
    margin,
    width,
  }) => {
    if (!slotContent) return <></>;
    const Slot = slotContent;

    // Resolve padding. `padding` kept its key across the refactor, so saved pages supply a raw CSS
    // string (e.g. '16px') rather than a BoxSpacing object. Passing a string through spacingToCss()
    // would destructure .top/.right/.bottom/.left off a string — all undefined — and emit the literal
    // "undefined undefined undefined undefined". Instead, pass the legacy string verbatim to
    // style.padding; CSS shorthand like '8px 16px' is already a valid padding declaration, preserving
    // exactly what the page rendered before.
    //
    // Flex-layout containers authored before the grouped field also have no `padding` key (pre-
    // grouped pages), in which case we fall back to DEFAULT_PADDING. The `paddingLegacy` prop that
    // was briefly present in this file was dead code — no saved page holds that key — and has been
    // removed.
    const resolvedPaddingCss: string = typeof padding === 'string' ? padding : spacingToCss(padding ?? DEFAULT_PADDING);

    // Resolve margin. `margin` is a new key: Puck's defaultProps injection fills it in the editor
    // but NOT in the published Render path. Legacy scalar margin props (marginTop/Right/Bottom/Left)
    // would therefore fire on a live page while being masked in the editor — the exact asymmetry
    // documented in section.tsx. Those props and the legacyMargin helper have been removed. Pages
    // that previously set them will adopt the grouped default; that is deliberate.
    const resolvedMargin = margin ?? DEFAULT_MARGIN;

    // flex-direction is NOT set inline. Inline styles cannot be overridden by media-query rules
    // without !important. Instead, --cms-flex-direction and --cms-flex-mobile-direction are set as
    // custom properties and StyledFlexSlot's stylesheet rule reads them, allowing the largePhone
    // breakpoint to swap the direction on mobile.
    // "Equal width" is only meaningful on a horizontal axis; on a vertical one the children are
    // already full width via align-items and an equal flex would equalise their heights instead.
    const itemFlexFor = (axisDirection: string): string =>
      itemSizing === 'equal' && axisDirection.startsWith('row') ? ITEM_SIZING_FLEX.equal : ITEM_SIZING_FLEX.content;

    const flexDirectionStyle: Record<string, string> = {
      '--cms-flex-direction': direction,
      '--cms-flex-mobile-direction': mobileStack === 'stack' ? 'column' : direction,
      // Consumed by StyledFlexSlot's `& > *` rules, one per breakpoint. `flex-basis` applies to the
      // MAIN axis, so an equal flex only means "equal width" while the axis is horizontal; in a
      // column it would equalise heights and stretch children into tall blocks. Each value is
      // therefore computed against the direction in force at that breakpoint. Unrecognised saved
      // values fall back to the natural-width flex, so a stale key cannot stretch a page's children.
      '--cms-flex-item-flex': itemFlexFor(direction),
      '--cms-flex-item-mobile-flex': itemFlexFor(mobileStack === 'stack' ? 'column' : direction),
    };

    // An explicit `min-height` must not be emitted for the no-minimum cases. Puck sizes an empty
    // drop zone by setting `--puck-slot-min-empty-height` on this same element and consuming it from
    // its own stylesheet; an inline `min-height: auto` beats that stylesheet rule, collapsing the
    // zone to zero height so nothing can be dragged into an empty container at all. develop guarded
    // this with an `isDefaultMinHeight` sentinel for exactly this reason — reintroducing the control
    // as presets has to keep the sentinel. `'0'` is covered too because that is the other value the
    // old raw-string field could hold.
    const resolvedMinHeight = lookup(CONTAINER_MIN_HEIGHTS, minHeight, minHeight);
    const NO_MINIMUM = ['auto', '0', '0px', ''];

    // Dropped options (e.g. 'row-reverse', 'wrap-reverse', 'baseline', 'space-around') are valid
    // CSS but are no longer in the offered option lists. They must still render verbatim. Casting to
    // React.CSSProperties here instead of narrowing the prop types keeps those saved values working.
    const slotStyle: React.CSSProperties = {
      // Guard: Puck does not inject defaultProps in the published Render path, only in the editor.
      // A legacy saved page that never set a `background` key arrives here with
      // `background === undefined`. backgroundStyle(undefined) returns
      // { backgroundColor: <surface token> } — white — which would paint a fill over whatever
      // sits behind this inner container. Omit the spread entirely when `background` is absent.
      ...(background ? backgroundStyle(background) : undefined),
      ...containerColorStyle(textColor),
      ...(flexDirectionStyle as React.CSSProperties),
      display: 'flex',
      flexWrap: wrap as React.CSSProperties['flexWrap'],
      gap: lookup(BLOCK_SPACING, gap, gap),
      alignItems,
      justifyContent,
      minHeight: NO_MINIMUM.includes(resolvedMinHeight) ? undefined : resolvedMinHeight,
      padding: resolvedPaddingCss,
      margin: spacingToCss(resolvedMargin),
      width: lookup(BLOCK_WIDTHS, width, width),
      // minWidth: 0 is the classic flexbox shrink fix; it belongs in the stylesheet, not a merchant control
      minWidth: 0,
      boxSizing: 'border-box',
    };

    return <Slot as={StyledFlexSlot} minEmptyHeight={128} style={slotStyle} />;
  },
};
